import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  MessageSquare, 
  Settings, 
  LogOut,
  Menu,
  X,
  BarChart3,
  Calendar,
  Bell,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../database/supabase';
import TeacherEvaluationAdmin from './TeacherEvaluationAdmin';

async function fetchAllRows(buildQuery) {
  const PAGE_SIZE = 1000;
  let allData = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error || !data || data.length === 0) break;
    allData = allData.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return allData;
}

// Admin Components
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Redirect if not admin
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Forms', href: '/admin/forms', icon: FileText },
    { name: 'Teacher Evaluation', href: '/admin/teacher-evaluation', icon: GraduationCap },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Announcements', href: '/admin/announcements', icon: MessageSquare },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 bg-gray-900">
          <h2 className="text-xl font-bold text-white">Admin Panel</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-blue-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={logout}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <Bell className="w-6 h-6 text-gray-400" />
              <div className="text-sm">
                <p className="font-medium text-gray-900">Welcome, {user.name}</p>
                <p className="text-gray-500">Administrator</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/forms" element={<FormsManagement />} />
            <Route path="/teacher-evaluation" element={<TeacherEvaluationAdmin />} />
            <Route path="/users" element={<UsersManagement />} />
            <Route path="/announcements" element={<AnnouncementsManagement />} />
            <Route path="/settings" element={<SettingsManagement />} />
          </Routes>
        </main>
      </div>
      
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Dashboard Home Component
const CAMPUSES = ['Gubat', 'Main'];

const DashboardHome = () => {
  const [selectedCampus, setSelectedCampus] = React.useState('');
  const [stats, setStats] = React.useState([
    { name: 'Total Evaluations Submitted', value: '0', icon: FileText, color: 'bg-blue-500' },
    { name: 'No. of Students', value: '0', icon: Users, color: 'bg-green-500' },
    { name: 'Pending Students Evaluation', value: '0', icon: Calendar, color: 'bg-yellow-500' },
    { name: 'Comments Sentiment Overall', value: 'N/A', icon: BarChart3, color: 'bg-purple-500' },
  ]);
  const [campusBreakdown, setCampusBreakdown] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadDashboardStats();
  }, [selectedCampus]); // eslint-disable-line

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      // Query pre-computed materialized views (fast — no full table scans)
      const [subRes, asgRes, sentRes] = await Promise.all([
        supabase.from('mv_campus_submission_stats').select('campus, total_submissions, students_submitted'),
        supabase.from('mv_campus_assigned_students').select('campus, students_assigned'),
        supabase.from('mv_campus_sentiment').select('campus, positive_count, negative_count, neutral_count'),
      ]);

      const submissionData = subRes.data || [];
      const assignedData   = asgRes.data  || [];
      const sentimentData  = sentRes.data || [];

      // Helper: sum a field across all campuses or find for a specific campus
      const sumField = (arr, field) =>
        selectedCampus
          ? (arr.find(r => r.campus === selectedCampus)?.[field] || 0)
          : arr.reduce((n, r) => n + (r[field] || 0), 0);

      const totalSubmissions  = sumField(submissionData, 'total_submissions');
      const studentsSubmitted = sumField(submissionData, 'students_submitted');
      const studentsAssigned  = sumField(assignedData,   'students_assigned');
      const pendingStudents   = Math.max(0, studentsAssigned - studentsSubmitted);

      // Sentiment aggregation
      const sentRows = selectedCampus
        ? sentimentData.filter(r => r.campus === selectedCampus)
        : sentimentData;
      const positive = sentRows.reduce((n, r) => n + (r.positive_count || 0), 0);
      const negative = sentRows.reduce((n, r) => n + (r.negative_count || 0), 0);
      const neutral  = sentRows.reduce((n, r) => n + (r.neutral_count  || 0), 0);

      let overallSentiment = 'Neutral';
      if (positive > negative && positive > neutral) overallSentiment = 'Positive';
      else if (negative > positive && negative > neutral) overallSentiment = 'Negative';

      // Per-campus breakdown cards
      const breakdown = CAMPUSES.map(campus => {
        const sub = submissionData.find(r => r.campus === campus);
        const asg = assignedData.find(r => r.campus === campus);
        const total     = asg?.students_assigned  || 0;
        const completed = sub?.students_submitted || 0;
        return {
          campus,
          submissions: sub?.total_submissions || 0,
          students:    total,
          completed,
          pending:     Math.max(0, total - completed),
        };
      });
      setCampusBreakdown(breakdown);

      setStats([
        { name: 'Total Evaluations Submitted', value: totalSubmissions.toString(),  icon: FileText, color: 'bg-blue-500' },
        { name: 'No. of Students',             value: studentsAssigned.toString(),  icon: Users,    color: 'bg-green-500' },
        { name: 'Pending Students Evaluation', value: pendingStudents.toString(),   icon: Calendar, color: 'bg-yellow-500' },
        { name: 'Comments Sentiment Overall',  value: overallSentiment, icon: BarChart3,
          color: overallSentiment === 'Positive' ? 'bg-green-500' : overallSentiment === 'Negative' ? 'bg-red-500' : 'bg-purple-500' },
      ]);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshViews = async () => {
    // Batch 1 = fast non-JSONB views, Batch 2 = campus sentiment (JSONB, Dashboard only)
    const batches = [1, 2];
    const toastId = toast.loading(`Refreshing views (1/${batches.length})…`);
    for (let i = 0; i < batches.length; i++) {
      toast.loading(`Refreshing views (${i + 1}/${batches.length})…`, { id: toastId });
      const { error } = await supabase.rpc('refresh_evaluation_views', { batch_num: batches[i] });
      if (error) {
        toast.dismiss(toastId);
        toast.error('Refresh failed: ' + error.message);
        return;
      }
    }
    toast.dismiss(toastId);
    toast.success('Data refreshed!');
    loadDashboardStats();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome to the Guidance Center Admin Panel</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedCampus}
            onChange={e => setSelectedCampus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white shadow-sm"
          >
            <option value="">All Campus</option>
            {CAMPUSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={handleRefreshViews}
            title="Refresh pre-computed statistics"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white shadow-sm hover:bg-gray-50"
          >
            ↺ Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat) => (
              <div key={stat.name} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Per-Campus Breakdown (shown when All Campus is selected) */}
          {!selectedCampus && campusBreakdown.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Submissions per Campus</h3>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {campusBreakdown.map(cb => (
                  <div key={cb.campus} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 text-lg mb-3">{cb.campus} Campus</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 rounded p-3 text-center">
                        <div className="text-2xl font-bold text-blue-700">{cb.submissions}</div>
                        <div className="text-xs text-blue-600 mt-1">Submissions</div>
                      </div>
                      <div className="bg-green-50 rounded p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">{cb.students}</div>
                        <div className="text-xs text-green-600 mt-1">Total Students</div>
                      </div>
                      <div className="bg-emerald-50 rounded p-3 text-center">
                        <div className="text-2xl font-bold text-emerald-700">{cb.completed}</div>
                        <div className="text-xs text-emerald-600 mt-1">Completed</div>
                      </div>
                      <div className="bg-yellow-50 rounded p-3 text-center">
                        <div className="text-2xl font-bold text-yellow-700">{cb.pending}</div>
                        <div className="text-xs text-yellow-600 mt-1">Pending</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Recent Activity */}
      <RecentEvaluations />
    </div>
  );
};

// Recent Evaluations Component
const RecentEvaluations = () => {
  const [recentEvaluations, setRecentEvaluations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadRecentEvaluations();
  }, []);

  const loadRecentEvaluations = async () => {
    try {
      const { data } = await supabase
        .from('teacher_evaluations')
        .select('id, submitted_at, student_accounts(student_id, name), teachers(name), subjects(code, name)')
        .order('submitted_at', { ascending: false })
        .limit(5);

      setRecentEvaluations(data || []);
    } catch (err) {
      console.error('Error loading recent evaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Evaluation Submissions</h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-800"></div>
            </div>
          ) : recentEvaluations.length > 0 ? (
            <div className="space-y-4">
              {recentEvaluations.map((evaluation) => (
                <div key={evaluation.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {evaluation.teachers?.name} - {evaluation.subjects?.code}
                    </p>
                    <p className="text-sm text-gray-500">
                      by {evaluation.student_accounts?.name || evaluation.student_accounts?.student_id}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(evaluation.submitted_at)}</p>
                  </div>
                  <span className="status-badge status-new">New</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent evaluations</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            <Link to="/admin/teacher-evaluation" className="block w-full btn-primary text-center">
              Manage Teacher Evaluations
            </Link>
            <Link to="/admin/users" className="block w-full btn-outline text-center">
              Manage Users
            </Link>
            <Link to="/admin/forms" className="block w-full btn-outline text-center">
              View Form Submissions
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other admin sections
const FormsManagement = () => {
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState('submissions');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ formId: '', status: '' });

  // Load forms and submissions from Supabase
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load all forms (including inactive ones for admin)
      const { data: formsData, error: formsError } = await supabase
        .from('forms')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (formsError) throw formsError;
      
      // Get submission counts separately
      const { data: submissionCounts, error: countsError } = await supabase
        .from('form_submissions')
        .select('form_id')
        .eq('status', 'submitted');
      
      if (countsError) throw countsError;
      
      // Process forms data to include submission counts
      const formsWithCounts = formsData.map(form => {
        const count = submissionCounts.filter(sub => sub.form_id === form.id).length;
        return {
          ...form,
          submissions_count: count
        };
      });
      
      setForms(formsWithCounts);
      
      // Load submissions with form and user details separately
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('form_submissions')
        .select(`
          id,
          form_id,
          user_id,
          status,
          submitted_at,
          data
        `)
        .order('submitted_at', { ascending: false })
        .limit(50);
      
      if (submissionsError) throw submissionsError;
      
      // Get form names
      const { data: formsLookup, error: formsLookupError } = await supabase
        .from('forms')
        .select('id, name, form_type');
      
      if (formsLookupError) throw formsLookupError;
      
      // Get user names
      const { data: usersLookup, error: usersLookupError } = await supabase
        .from('users')
        .select('id, name, email');
      
      if (usersLookupError) throw usersLookupError;
      
      // Combine the data
      const enrichedSubmissions = submissionsData.map(submission => {
        const form = formsLookup.find(f => f.id === submission.form_id);
        const user = usersLookup.find(u => u.id === submission.user_id);
        
        return {
          ...submission,
          forms: form,
          users: user
        };
      });
      
      setSubmissions(enrichedSubmissions || []);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Toggle form active status
  const toggleFormStatus = async (formId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('forms')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', formId);
      
      if (error) throw error;
      
      // Update local state
      setForms(forms.map(form => 
        form.id === formId 
          ? { ...form, is_active: !currentStatus }
          : form
      ));
      
      toast.success(`Form ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Error updating form status:', err);
      toast.error('Failed to update form status');
    }
  };

  // Update submission status
  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const { error } = await supabase
        .from('form_submissions')
        .update({ 
          status: newStatus,
          reviewed_at: newStatus === 'reviewed' ? new Date().toISOString() : null
        })
        .eq('id', submissionId);
      
      if (error) throw error;
      
      // Update local state
      setSubmissions(submissions.map(sub => 
        sub.id === submissionId 
          ? { ...sub, status: newStatus }
          : sub
      ));
      
      toast.success('Submission status updated');
    } catch (err) {
      console.error('Error updating submission:', err);
      toast.error('Failed to update submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter submissions based on selected filters
  const filteredSubmissions = submissions.filter(submission => {
    if (filters.formId && submission.form_id !== filters.formId) return false;
    if (filters.status && submission.status !== filters.status) return false;
    return true;
  });

  // View submission details
  const viewSubmission = async (submissionId) => {
    try {
      const { data, error } = await supabase
        .from('form_submissions')
        .select(`
          *,
          forms(name, form_type, fields)
        `)
        .eq('id', submissionId)
        .single();
      
      if (error) throw error;
      setSelectedSubmission(data);
    } catch (err) {
      console.error('Error loading submission:', err);
      toast.error('Failed to load submission details');
    }
  };

  // Create new form
  const createForm = async (formData) => {
    try {
      const { data, error } = await supabase
        .from('forms')
        .insert([{
          name: formData.name,
          description: formData.description,
          form_type: formData.form_type,
          fields: formData.fields || {},
          is_active: true
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      setForms([data, ...forms]);
      setShowCreateForm(false);
      toast.success('Form created successfully');
    } catch (err) {
      console.error('Error creating form:', err);
      toast.error('Failed to create form');
    }
  };

  // Update form
  const updateForm = async (formId, formData) => {
    try {
      const { data, error } = await supabase
        .from('forms')
        .update({
          name: formData.name,
          description: formData.description,
          fields: formData.fields,
          updated_at: new Date().toISOString()
        })
        .eq('id', formId)
        .select()
        .single();
      
      if (error) throw error;
      
      setForms(forms.map(form => form.id === formId ? data : form));
      setShowEditForm(null);
      toast.success('Form updated successfully');
    } catch (err) {
      console.error('Error updating form:', err);
      toast.error('Failed to update form');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-800 mb-2">Error Loading Data</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Forms Management</h1>
        <p className="text-gray-600">Manage form templates and view submissions</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'submissions'
                ? 'border-blue-800 text-blue-800'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Form Submissions ({filteredSubmissions.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-800 text-blue-800'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Form Templates ({forms.length})
          </button>
        </nav>
      </div>

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent Submissions</h3>
              <div className="flex space-x-2">
                <select 
                  className="form-input text-sm"
                  value={filters.formId}
                  onChange={(e) => setFilters({...filters, formId: e.target.value})}
                >
                  <option value="">All Forms</option>
                  {forms.map(form => (
                    <option key={form.id} value={form.id}>{form.name}</option>
                  ))}
                </select>
                <select 
                  className="form-input text-sm"
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="in_review">In Review</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="archived">Archived</option>
                </select>
                <button onClick={loadData} className="btn-outline text-sm">
                  Refresh
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Form & Submitter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {submission.forms?.name || 'Unknown Form'}
                        </div>
                        <div className="text-sm text-gray-500">
                          by {submission.users?.name || submission.users?.email || 'Anonymous'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(submission.submitted_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={submission.status}
                        onChange={(e) => updateSubmissionStatus(submission.id, e.target.value)}
                        className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${getStatusColor(submission.status)}`}
                      >
                        <option value="submitted">SUBMITTED</option>
                        <option value="in_review">IN REVIEW</option>
                        <option value="reviewed">REVIEWED</option>
                        <option value="archived">ARCHIVED</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => viewSubmission(submission.id)}
                        className="text-blue-800 hover:text-red-900 mr-3"
                      >
                        View
                      </button>
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        Export
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredSubmissions.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No submissions found</h3>
                <p className="text-gray-500">No form submissions match your current filters.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          <div className="mb-6">
            <button 
              onClick={() => setShowCreateForm(true)}
              className="btn-primary"
            >
              <FileText className="w-4 h-4 mr-2" />
              Create New Form
            </button>
          </div>
          
          <div className="grid gap-6">
            {forms.map((form) => (
              <div key={form.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-lg font-medium text-gray-900">{form.name}</h3>
                      <span className={`ml-3 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        form.is_active ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Form Type: {form.form_type}</p>
                    <p className="text-sm text-gray-500">{form.submissions_count} submissions</p>
                    <p className="text-sm text-gray-500">Created: {formatDate(form.created_at)}</p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setShowEditForm(form)}
                      className="btn-outline text-sm"
                    >
                      Edit
                    </button>
                    <button className="btn-outline text-sm">Preview</button>
                    <button className="btn-outline text-sm">Export Data</button>
                    <button 
                      onClick={() => toggleFormStatus(form.id, form.is_active)}
                      className={`text-sm px-3 py-1 rounded ${
                        form.is_active 
                          ? 'bg-blue-100 text-blue-800 hover:bg-red-200' 
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {form.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {forms.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No forms found</h3>
                <p className="text-gray-500">Create your first form to get started.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedSubmission.forms?.name || 'Form Submission'}
                </h3>
                <button 
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Submission Details</h4>
                  <p className="text-sm text-gray-600">Submitted: {formatDate(selectedSubmission.submitted_at)}</p>
                  <p className="text-sm text-gray-600">Status: {selectedSubmission.status}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Form Data</h4>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedSubmission.data, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <CreateFormModal 
          onClose={() => setShowCreateForm(false)}
          onSubmit={createForm}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && (
        <EditFormModal 
          form={showEditForm}
          onClose={() => setShowEditForm(null)}
          onSubmit={(formData) => updateForm(showEditForm.id, formData)}
        />
      )}
    </div>
  );
};

const UsersManagement = () => (
  <div>
    <h1 className="text-2xl font-bold text-gray-900 mb-6">Users Management</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">User account management interface will be implemented here.</p>
    </div>
  </div>
);

const AnnouncementsManagement = () => (
  <div>
    <h1 className="text-2xl font-bold text-gray-900 mb-6">Announcements</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">Announcements management interface will be implemented here.</p>
    </div>
  </div>
);

const SettingsManagement = () => (
  <div>
    <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">System settings and configuration will be implemented here.</p>
    </div>
  </div>
);

// Admin Login Component
const AdminLogin = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Check if already logged in
  React.useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Mock authentication - replace with real authentication
      if (credentials.email === 'admin@slmcs.ph' && credentials.password === 'passAdmin2026') {
        const adminUser = { 
          id: '1', 
          email: credentials.email, 
          name: 'Administrator', 
          role: 'admin' 
        };
        await login(adminUser);
        navigate('/admin');
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      toast.error('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Sign in to access the admin panel</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-input"
              value={credentials.email}
              onChange={(e) => setCredentials({...credentials, email: e.target.value})}
              placeholder="Enter your email"
              required
            />
          </div>
          
          <div>
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              placeholder="Enter your password"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-blue-800 hover:text-red-900">
            ← Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export { AdminDashboard, AdminLogin };
export default AdminDashboard;

// Create Form Modal Component
const CreateFormModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    form_type: '',
    fields: { sections: [] }
  });
  const [activeTab, setActiveTab] = useState('basic');

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      title: 'New Field',
      type: 'TEXT',
      required: false
    };
    
    const updatedFields = { ...formData.fields };
    if (!updatedFields.sections) updatedFields.sections = [];
    if (updatedFields.sections.length === 0) {
      updatedFields.sections.push({ id: 'section_1', title: 'Form Fields', fields: [] });
    }
    updatedFields.sections[0].fields.push(newField);
    setFormData({ ...formData, fields: updatedFields });
  };

  const updateField = (fieldIndex, updates) => {
    const updatedFields = { ...formData.fields };
    updatedFields.sections[0].fields[fieldIndex] = { ...updatedFields.sections[0].fields[fieldIndex], ...updates };
    setFormData({ ...formData, fields: updatedFields });
  };

  const removeField = (fieldIndex) => {
    const updatedFields = { ...formData.fields };
    updatedFields.sections[0].fields.splice(fieldIndex, 1);
    setFormData({ ...formData, fields: updatedFields });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Create New Form</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex px-6">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'basic' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500'
              }`}
            >
              Basic Info
            </button>
            <button
              onClick={() => setActiveTab('fields')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'fields' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500'
              }`}
            >
              Form Fields
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="form-label">Form Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              <div>
                <label className="form-label">Form Type *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.form_type}
                  onChange={(e) => setFormData({...formData, form_type: e.target.value})}
                  placeholder="e.g., student_assessment"
                  required
                />
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Form Fields</h4>
                <button type="button" onClick={addField} className="btn-outline text-sm">
                  Add Field
                </button>
              </div>
              
              {formData.fields.sections?.[0]?.fields?.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label text-sm">Field Title</label>
                        <input
                          type="text"
                          className="form-input text-sm"
                          value={field.title}
                          onChange={(e) => updateField(index, { title: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="form-label text-sm">Field Type</label>
                        <select
                          className="form-input text-sm"
                          value={field.type}
                          onChange={(e) => updateField(index, { type: e.target.value })}
                        >
                          <option value="TEXT">Text</option>
                          <option value="PARAGRAPH_TEXT">Paragraph</option>
                          <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                          <option value="DATE">Date</option>
                          <option value="SCALE">Scale (1-5)</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="text-red-600 hover:text-blue-800 ml-3"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-600">Required field</label>
                  </div>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-8">No fields added yet. Click "Add Field" to get started.</p>
              )}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Create Form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Form Modal Component
const EditFormModal = ({ form, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: form.name || '',
    description: form.description || '',
    fields: form.fields || { sections: [] }
  });
  const [activeTab, setActiveTab] = useState('basic');

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      title: 'New Field',
      type: 'TEXT',
      required: false
    };
    
    const updatedFields = { ...formData.fields };
    if (!updatedFields.sections) updatedFields.sections = [];
    if (updatedFields.sections.length === 0) {
      updatedFields.sections.push({ id: 'section_1', title: 'Form Fields', fields: [] });
    }
    updatedFields.sections[0].fields.push(newField);
    setFormData({ ...formData, fields: updatedFields });
  };

  const updateField = (fieldIndex, updates) => {
    const updatedFields = { ...formData.fields };
    if (!updatedFields.sections?.[0]?.fields) return;
    updatedFields.sections[0].fields[fieldIndex] = { ...updatedFields.sections[0].fields[fieldIndex], ...updates };
    setFormData({ ...formData, fields: updatedFields });
  };

  const removeField = (fieldIndex) => {
    const updatedFields = { ...formData.fields };
    if (!updatedFields.sections?.[0]?.fields) return;
    updatedFields.sections[0].fields.splice(fieldIndex, 1);
    setFormData({ ...formData, fields: updatedFields });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Edit Form</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex px-6">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'basic' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500'
              }`}
            >
              Basic Info
            </button>
            <button
              onClick={() => setActiveTab('fields')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'fields' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500'
              }`}
            >
              Form Fields ({formData.fields.sections?.reduce((total, section) => total + (section.fields?.length || 0), 0) || 0})
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="form-label">Form Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-6">
              {formData.fields.sections?.map((section, sectionIndex) => (
                <div key={section.id} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-900">{section.title}</h4>
                    <span className="text-sm text-gray-500">{section.fields?.length || 0} fields</span>
                  </div>
                  
                  <div className="space-y-3">
                    {section.fields?.map((field, fieldIndex) => (
                      <div key={field.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div>
                              <label className="form-label text-sm">Field Title</label>
                              <input
                                type="text"
                                className="form-input text-sm"
                                value={field.title}
                                onChange={(e) => {
                                  const updatedFields = { ...formData.fields };
                                  updatedFields.sections[sectionIndex].fields[fieldIndex].title = e.target.value;
                                  setFormData({ ...formData, fields: updatedFields });
                                }}
                              />
                            </div>
                            <div>
                              <label className="form-label text-sm">Field Type</label>
                              <select
                                className="form-input text-sm"
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = { ...formData.fields };
                                  updatedFields.sections[sectionIndex].fields[fieldIndex].type = e.target.value;
                                  setFormData({ ...formData, fields: updatedFields });
                                }}
                              >
                                <option value="TEXT">Text</option>
                                <option value="PARAGRAPH_TEXT">Paragraph</option>
                                <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                                <option value="LIST">List/Dropdown</option>
                                <option value="DATE">Date</option>
                                <option value="SCALE">Scale (1-5)</option>
                              </select>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedFields = { ...formData.fields };
                              updatedFields.sections[sectionIndex].fields.splice(fieldIndex, 1);
                              setFormData({ ...formData, fields: updatedFields });
                            }}
                            className="text-red-600 hover:text-blue-800 ml-3"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center mt-2">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => {
                              const updatedFields = { ...formData.fields };
                              updatedFields.sections[sectionIndex].fields[fieldIndex].required = e.target.checked;
                              setFormData({ ...formData, fields: updatedFields });
                            }}
                            className="mr-2"
                          />
                          <label className="text-sm text-gray-600">Required field</label>
                        </div>
                        {field.options && (
                          <div className="mt-2">
                            <label className="form-label text-sm">Options</label>
                            <textarea
                              className="form-input text-sm"
                              rows="2"
                              value={Array.isArray(field.options) ? field.options.join(', ') : ''}
                              onChange={(e) => {
                                const updatedFields = { ...formData.fields };
                                updatedFields.sections[sectionIndex].fields[fieldIndex].options = e.target.value.split(', ');
                                setFormData({ ...formData, fields: updatedFields });
                              }}
                              placeholder="Option 1, Option 2, Option 3"
                            />
                          </div>
                        )}
                      </div>
                    )) || (
                      <p className="text-gray-500 text-center py-4">No fields in this section</p>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const newField = {
                        id: `field_${Date.now()}`,
                        title: 'New Field',
                        type: 'TEXT',
                        required: false
                      };
                      const updatedFields = { ...formData.fields };
                      if (!updatedFields.sections[sectionIndex].fields) {
                        updatedFields.sections[sectionIndex].fields = [];
                      }
                      updatedFields.sections[sectionIndex].fields.push(newField);
                      setFormData({ ...formData, fields: updatedFields });
                    }}
                    className="btn-outline text-sm mt-3"
                  >
                    Add Field to {section.title}
                  </button>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-8">No sections found in this form.</p>
              )}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Update Form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};